/**
 * Atrium product showcase — appears just above the public footer so visitors
 * who reach the bottom of the property's transformation story understand
 * what platform powers it. Includes the marketing description we authored
 * during the rebrand and a CTA to learn more.
 */
import { useState } from 'react';
import { Sparkles, Map as MapIcon, Calendar, Image as ImageIcon, MessageCircle, ArrowRight } from 'lucide-react';
import AtriumLogo from './AtriumLogo';
import DemoRequestDialog from './DemoRequestDialog';

const PILLARS = [
  { icon: MapIcon,        label: 'Interactive resort maps',       sub: 'Drop hotspots, render before/after' },
  { icon: Calendar,       label: '30-milestone roadmap',          sub: 'Phase-coded against a firm reopening' },
  { icon: ImageIcon,      label: 'Plans · CADs · AI renders',     sub: 'Technical library with auto previews' },
  { icon: MessageCircle,  label: 'In-app team chat',              sub: 'Ownership, operator, contractors aligned' },
];

export default function AtriumPromoSection() {
  const [demoOpen, setDemoOpen] = useState(false);

  return (
    <section
      data-testid="atrium-promo-section"
      id="atrium-promo"
      className="py-20 px-4 sm:px-6 lg:px-10 relative overflow-hidden"
      style={{ backgroundColor: '#0E1F2C', color: '#EAE5D9' }}
    >
      {/* Decorative accent bar */}
      <div
        aria-hidden
        className="absolute inset-x-0 top-0 h-[3px]"
        style={{
          backgroundImage: 'linear-gradient(90deg,#F59E0B 0%,#84CC16 20%,#14B8A6 40%,#3B82F6 60%,#8B5CF6 80%,#EC4899 100%)',
        }}
      />

      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-10 lg:gap-16 items-center">
          {/* Left: marketing copy */}
          <div>
            {/* Big Atrium lockup at the top — logo is presented dark-mode
                friendly with light accent + light text. */}
            <div className="mb-5">
              <AtriumLogo variant="inline" size={48} accent="#A3D4D4" text="#FFFFFF" />
            </div>

            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full mb-5" style={{ backgroundColor: 'rgba(163,212,212,0.15)', color: '#A3D4D4' }}>
              <Sparkles className="w-3.5 h-3.5" />
              <span className="text-[10px] font-bold tracking-[0.25em] uppercase" style={{ fontFamily: 'Manrope' }}>
                Powered by Atrium
              </span>
            </div>

            <h2
              className="text-4xl sm:text-5xl lg:text-6xl mb-3 leading-[1.05]"
              style={{ fontFamily: "'Cormorant Garamond', serif", color: '#FFFFFF', fontWeight: 500 }}
            >
              Where every renovation comes together.
            </h2>

            <p
              className="text-base sm:text-lg italic mb-6"
              style={{ fontFamily: "'Cormorant Garamond', serif", color: '#A3D4D4' }}
            >
              Hotel operations &amp; project management, made visible.
            </p>

            <div className="space-y-4 text-[15px] leading-relaxed" style={{ fontFamily: 'Manrope', color: '#EAE5D9', maxWidth: '60ch' }}>
              <p>
                <span className="font-bold" style={{ color: 'white' }}>Atrium is the command center for the full property lifecycle.</span>{' '}
                The everyday operations of an open hotel, the pre-opening of a new build, every reconstruction and renovation in between — all running inside a single workspace that an owner, VP, asset manager, or multi-property director can read at a glance.
              </p>
              <p>
                Upload technical plans, drop hotspots on resort maps, generate AI renders of post-renovation suites, manage daily checklists, CapEx, and roadmaps, and watch any project unfold against a firm date. Atrium gives leadership a public-facing window and operations a private command center —{' '}
                <span style={{ color: '#A3D4D4' }}>at the same time.</span>
              </p>
              <p className="italic" style={{ color: '#A3D4D4' }}>
                One source of truth, every day of the property's life.
              </p>
            </div>

            <div className="mt-8 flex flex-wrap gap-3">
              <button
                onClick={() => setDemoOpen(true)}
                data-testid="atrium-demo-cta"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-xs font-bold transition-all hover:-translate-y-0.5"
                style={{ backgroundColor: '#A3D4D4', color: '#0E1F2C', fontFamily: 'Manrope' }}
              >
                Request a demo for your property <ArrowRight className="w-3.5 h-3.5" />
              </button>
              <span
                className="inline-flex items-center gap-2 px-4 py-3 rounded-full text-[10px] font-bold uppercase tracking-[0.2em]"
                style={{ backgroundColor: 'rgba(163,212,212,0.08)', color: '#7A8C8C', fontFamily: 'Manrope' }}
              >
                Day-to-Day Ops · Renovations · Rebuilds · New Openings
              </span>
            </div>
          </div>

          {/* Right: pillar cards */}
          <div className="grid grid-cols-2 gap-3">
            {PILLARS.map((p, i) => (
              <div
                key={p.label}
                data-testid={`atrium-pillar-${i}`}
                className="p-4 rounded-2xl flex flex-col gap-2 transition-transform hover:-translate-y-0.5"
                style={{
                  backgroundColor: 'rgba(163,212,212,0.06)',
                  border: '1px solid rgba(163,212,212,0.12)',
                  backdropFilter: 'blur(8px)',
                }}
              >
                <span
                  className="w-9 h-9 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: 'rgba(163,212,212,0.12)', color: '#A3D4D4' }}
                >
                  <p.icon className="w-4 h-4" />
                </span>
                <p className="text-[13px] font-bold leading-tight" style={{ color: 'white', fontFamily: 'Manrope' }}>
                  {p.label}
                </p>
                <p className="text-[11px] leading-snug" style={{ color: '#7A8C8C', fontFamily: 'Manrope' }}>
                  {p.sub}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <DemoRequestDialog open={demoOpen} onClose={setDemoOpen} />
    </section>
  );
}
